import { useEffect, useState, type ReactNode } from 'react';
import { Modal } from '@/components/kit';

/**
 * THE TWO EDITORS EVERY SETTINGS FIELD USES.
 *
 * The kit's `Field` is a DISPLAY field — an overline label over a value, and
 * `onEdit` makes the whole card the control. It deliberately has no input, so
 * the editor is a separate thing, and it must be its OWN component with its own
 * state: a form whose state lives in the page re-renders the page on every
 * keystroke, and inside a docked pane that means the shell republishes the pane
 * on every keystroke (BRIEF §4.3).
 *
 * Both open on the CURRENT value and hand back a new one; neither saves. The
 * screen collects the edits and the top bar's one Save writes them, which is
 * what the board's "⚠ unsaved" chip is counting.
 */

export function TextEditor({
  open,
  title,
  sub,
  value,
  placeholder,
  type = 'text',
  onClose,
  onDone,
}: {
  open: boolean;
  title: string;
  sub?: string;
  value: string;
  placeholder?: string;
  type?: 'text' | 'tel' | 'email' | 'url' | 'date';
  onClose: () => void;
  onDone: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  /* Re-seed when the editor is re-opened on a different field or a changed
     value; without this the second field edited opens on the first one's text. */
  useEffect(() => { if (open) setDraft(value); }, [open, value]);

  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      sub={sub}
      width={460}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => { onDone(draft.trim()); onClose(); }}>
            Done
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" htmlFor="cm-setting-input" style={{ display: 'block', marginTop: 12 }}>
        {title}
      </label>
      <input
        id="cm-setting-input"
        className="cm-inp"
        type={type}
        value={draft}
        placeholder={placeholder}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { onDone(draft.trim()); onClose(); } }}
      />
    </Modal>
  );
}

export interface PickOption {
  value: string;
  label: ReactNode;
  /** A second line under the label — what choosing this one means. */
  note?: ReactNode;
  /** Text the search box matches (a long list — countries). */
  search?: string;
}

export function PickerModal({
  open,
  title,
  sub,
  options,
  value,
  onClose,
  onPick,
  extra,
  searchable,
}: {
  open: boolean;
  title: string;
  sub?: string;
  options: PickOption[];
  value: string;
  onClose: () => void;
  onPick: (next: string) => void;
  /** An escape hatch row under the list — "Type another…". */
  extra?: ReactNode;
  /** A search box above a long list; matches each option's `search`. */
  searchable?: boolean;
}) {
  const [q, setQ] = useState('');
  if (!open) return null;
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? options.filter((o) => (o.search ?? String(o.value)).toLowerCase().includes(needle))
    : options;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      sub={sub}
      width={460}
      foot={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}
    >
      {searchable ? (
        <input
          className="cm-inp"
          style={{ marginTop: 12 }}
          autoFocus
          value={q}
          placeholder="Search"
          aria-label="Search"
          onChange={(e) => setQ(e.target.value)}
        />
      ) : null}
      <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0', ...(searchable ? { maxHeight: 360, overflowY: 'auto' as const } : {}) }}>
        {shown.map((o) => (
          <button key={o.value} type="button" className="mi"
            onClick={() => { onPick(o.value); onClose(); }}>
            <span className="grow">
              {o.label}
              {o.note ? <span className="t-cap mut" style={{ display: 'block' }}>{o.note}</span> : null}
            </span>
            {o.value === value ? <span className="t-cap brand w6">chosen</span> : null}
          </button>
        ))}
        {extra}
      </div>
    </Modal>
  );
}

/**
 * THE PENCIL ON A COLOUR CARD (board 17).
 *
 * A hex is typed as often as it is picked — a brand book gives you `#0c87f0`,
 * not a position in a gradient — so the editor carries BOTH: the platform's
 * own colour well and the text field, bound to one value. `Done` is refused
 * while the text is not a `#rrggbb`, because the backend refuses it too
 * (`TenantBrandingService` validates against the same shape) and a Save that
 * 400s after the modal closed is a worse answer than a disabled button.
 */
const HEX = /^#[0-9a-fA-F]{6}$/;

export function ColorEditor({
  open,
  title,
  sub,
  value,
  onClose,
  onDone,
}: {
  open: boolean;
  title: string;
  sub?: string;
  value: string;
  onClose: () => void;
  onDone: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (open) setDraft(value); }, [open, value]);

  if (!open) return null;
  const ok = HEX.test(draft.trim());
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      sub={sub}
      width={460}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!ok}
            onClick={() => { onDone(draft.trim().toLowerCase()); onClose(); }}>
            Done
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" htmlFor="cm-colour-input" style={{ display: 'block', marginTop: 12 }}>
        {title}
      </label>
      <div className="flex" style={{ gap: 10, marginTop: 6 }}>
        <input
          type="color"
          aria-label={`${title} — colour well`}
          value={ok ? draft.trim() : '#000000'}
          onChange={(e) => setDraft(e.target.value)}
          style={{ width: 44, height: 44, borderRadius: 10, border: 0, padding: 0, cursor: 'pointer' }}
        />
        <input
          id="cm-colour-input"
          className="cm-inp grow"
          type="text"
          value={draft}
          placeholder="#0c87f0"
          autoFocus
          style={{ margin: 0 }}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && HEX.test(draft.trim())) {
              onDone(draft.trim().toLowerCase());
              onClose();
            }
          }}
        />
      </div>
      <p className={ok ? 't-cap mut' : 't-cap red'} style={{ marginTop: 6 }}>
        {ok ? 'six hex digits, the way a brand book writes them' : 'a colour is #rrggbb — six hex digits'}
      </p>
    </Modal>
  );
}
